import { Fragment, useEffect, useRef } from 'react'
import {
  Container,
  Col,
  Row,
  Stack,
  Visible
} from '@leroy-merlin-br/backyard-react'
import { useInstantSearch } from 'react-instantsearch'

import { useLoyaltyProfile } from 'shared/hooks'

import { onProductImpressionDataLayer } from 'site/AlgoliaEntryKey/utils/data-layer'
import WishlistProviderMethods from 'site/Wishlist/provider'

import {
  SortBy,
  Products,
  Filters,
  FiltersMobile,
  BreadcrumbBar,
  TopContent,
  NoResultsSearch,
  EntryKeySkeleton
} from './components'
import { useAlgoliaContext } from './context/algolia-context'

interface AlgoliaEntryKeyProps {
  type: 'search' | 'mappedSearch' | 'entryKey'
  apiDomain: string
  categoryId: string
  isSearchResultPage: boolean
  indexName: string
}

const AlgoliaEntryKey = ({
  apiDomain,
  categoryId,
  isSearchResultPage,
  indexName
}: AlgoliaEntryKeyProps) => {
  const { results, type, gaTrack, query, userResources, hits } =
    useAlgoliaContext()
  const { status } = useInstantSearch()

  const breadcrumbRef = useRef()

  const { isLoading: isLoadingLoyaltyProfile } = useLoyaltyProfile()

  const shouldShowShell =
    !userResources || (isLoadingLoyaltyProfile && status === 'loading')

  const breadcrumbProps = {
    apiDomain,
    isSearchResultPage,
    categoryId
  }

  useEffect(() => {
    if (hits.length && (!shouldShowShell || !query)) {
      const timer = setTimeout(() => {
        onProductImpressionDataLayer({
          products: hits,
          region: userResources?.region,
          gaTrack: gaTrack,
          type,
          term: query,
          page: results?.page ? results?.page + 1 : 1
        })
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [
    gaTrack,
    hits,
    results,
    shouldShowShell,
    query,
    type,
    userResources?.region
  ])

  if (shouldShowShell || results?.query !== results?._state.query) {
    return <EntryKeySkeleton />
  }

  if (status === 'idle' && results?.nbHits === 0) {
    return (
      <Fragment>
        <BreadcrumbBar {...breadcrumbProps} ref={breadcrumbRef} />
        <NoResultsSearch isSearch={isSearchResultPage} />
      </Fragment>
    )
  }

  return (
    <Fragment>
      <BreadcrumbBar {...breadcrumbProps} ref={breadcrumbRef} />
      <TopContent />
      <Container>
        <Row align="flex-start">
          <Visible when={['giga', 'tera']}>
            <Col size={{ giga: 3 }}>
              <Stack space="mega">
                <SortBy indexName={indexName} />
                <Filters />
              </Stack>
            </Col>
          </Visible>
          <Col size={{ giga: 9, mega: 8, kilo: 4 }}>
            <WishlistProviderMethods>
              <Products hits={hits} />
            </WishlistProviderMethods>
          </Col>
        </Row>
      </Container>
      <FiltersMobile indexName={indexName} />
    </Fragment>
  )
}

export default AlgoliaEntryKey
